import React, { useCallback, useEffect, useRef, useState, ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { Analytics } from "@vercel/analytics/react";
import { saveToGoogleSheets } from "./services/googleSheets";
import { Copy, Check } from "lucide-react";
import "./App.css";

// 스크롤 애니메이션 커스텀 훅
const useScrollAnimation = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // 한 번 보이면 계속 보이는 상태 유지
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      {
        threshold: 0.1,
        rootMargin: "-35% 0px -35% 0px", // 화면 중앙 30% 영역에 들어와야 트리거
      }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return { ref, isVisible };
};

// 애니메이션 래퍼 컴포넌트
interface AnimatedSectionProps {
  children: ReactNode;
  className?: string;
  delay?: number;
}

const AnimatedSection = ({ children, className = "", delay = 0 }: AnimatedSectionProps) => {
  const { ref, isVisible } = useScrollAnimation();

  const delayClass = delay > 0 ? `animate-delay-${delay}` : "";

  return (
    <div ref={ref} className={`animate-on-scroll ${isVisible ? "is-visible" : ""} ${delayClass} ${className}`}>
      {children}
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* 루트 경로는 /wedding/invitation으로 리다이렉트 */}
        <Route path="/" element={<Navigate to="/wedding/invitation" replace />} />

        {/* 청첩장 페이지 */}
        <Route path="/wedding/invitation" element={<WeddingInvitationPage />} />

        {/* 404 페이지 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </BrowserRouter>
  );
}

// Wedding Invitation Page - 청첩장 메인 페이지
const WeddingInvitationPage = () => {
  const [isRSVPModalOpen, setIsRSVPModalOpen] = useState(false);

  // 페이지 로딩 0.5초 후 자동으로 RSVP 모달 부드럽게 열기
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsRSVPModalOpen(true);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen font-serif">
      {/* 1. Wedding Day 배너 - 첫 화면은 애니메이션 없이 */}
      <WeddingDayBanner src="/images/배너.jpeg" />
      <WeddingDayBanner src="/images/배너이미지1.jpeg" />

      {/* 2. 카메라 커플 사진 - 스크롤 시 fadeInUp */}
      <AnimatedSection>
        <WeddingDayBanner src="/images/배너이미지2.jpeg" />
      </AnimatedSection>

      {/* Main Content */}
      <main className="relative m-0">
        {/* 2. 표지 영역 */}
        {/* <HeroSection /> */}

        {/* 3. 어린시절 사진 영역 */}
        <AnimatedSection>
          <ChildhoodPhotosSection />
        </AnimatedSection>

        {/* 4. 인스타 피드 스타일 갤러리 */}
        <AnimatedSection>
          <InstagramGallerySection />
        </AnimatedSection>

        {/* 6. 캘린더 영역 */}
        <AnimatedSection>
          <CalendarSection />
        </AnimatedSection>

        {/* 7. 카카오 지도 영역 */}
        <AnimatedSection>
          <LocationInfo />
        </AnimatedSection>

        {/* 8. 계좌번호 영역 */}
        <AnimatedSection>
          <AccountSection />
        </AnimatedSection>

        {/* 9. 참석 여부 영역 - 플로팅 버튼으로 대체 */}
        {/* <RSVPSection /> */}
      </main>

      {/* Floating Music Control */}
      <MusicControl />

      {/* Floating RSVP Button */}
      <RSVPFloatingButton onClick={() => setIsRSVPModalOpen(true)} />

      {/* RSVP Modal */}
      {isRSVPModalOpen && <RSVPModal onClose={() => setIsRSVPModalOpen(false)} />}

      {/* Vercel Speed Insights */}
      <SpeedInsights />

      {/* Vercel Web Analytics */}
      <Analytics />
    </div>
  );
};

// 404 Not Found Page
const NotFoundPage = () => {
  return (
    <div className="min-h-screen bg-bg-dark font-serif flex items-center justify-center px-4">
      <div className="text-center">
        <h1 className="text-6xl md:text-8xl font-light text-primary-pink mb-4">404</h1>
        <p className="text-xl md:text-2xl text-white mb-8">페이지를 찾을 수 없습니다</p>
        <a
          href="/wedding/invitation"
          className="inline-block px-6 py-3 bg-primary-pink text-white rounded-lg hover:bg-primary-pink-dark transition-colors"
        >
          청첩장으로 이동
        </a>
      </div>
    </div>
  );
};

// 2. Wedding Day 배너 이미지 영역
const WeddingDayBanner = ({ src }: { src: string }) => (
  <section className="py-0 bg-zinc-900">
    <img src={src} alt="Our Wedding Day" className="w-full max-w-2xl mx-auto h-auto block" />
  </section>
);

// 3. 어린시절 사진 영역 (신랑 + 신부)
const ChildhoodPhotosSection = () => (
  <section className="bg-zinc-900">
    <img
      src="/images/childhood-photos.jpeg"
      alt="little seungtae & little sumin"
      className="w-full max-w-2xl mx-auto shadow-2xl block"
    />
  </section>
);

// 4. 인스타 피드 스타일 갤러리
const InstagramGallerySection = () => {
  const images = [
    "KakaoTalk_Photo_2026-01-06-00-54-24 001.jpeg",
    "KakaoTalk_Photo_2026-01-06-00-54-25 002.jpeg",
    "KakaoTalk_Photo_2026-01-06-00-54-25 003.jpeg",
    "KakaoTalk_Photo_2026-01-09-00-17 004.jpeg",
    "KakaoTalk_Photo_2026-01-09-00-18 005.jpeg",
    "KakaoTalk_Photo_2026-01-09-00-18 006.jpeg",
    "KakaoTalk_Photo_2026-01-09-00-18 007.jpeg",
    "KakaoTalk_Photo_2026-01-09-00-19 008.jpeg",
    "KakaoTalk_Photo_2026-01-09-00-20 009.jpeg",
    "KakaoTalk_Photo_2026-01-09-00-21 010.jpeg",
    "KakaoTalk_Photo_2026-01-09-00-21 011.jpeg",
    "KakaoTalk_Photo_2026-01-09-00-22 012.jpeg",
  ];

  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // 이미지 클릭 핸들러
  const openLightbox = (index: number) => {
    setSelectedImageIndex(index);
  };

  // 라이트박스 닫기
  const closeLightbox = useCallback(() => {
    setSelectedImageIndex(null);
  }, []);

  // 이전 이미지
  const showPrevImage = useCallback(() => {
    if (selectedImageIndex !== null) {
      setSelectedImageIndex((selectedImageIndex - 1 + images.length) % images.length);
    }
  }, [selectedImageIndex, images.length]);

  // 다음 이미지
  const showNextImage = useCallback(() => {
    if (selectedImageIndex !== null) {
      setSelectedImageIndex((selectedImageIndex + 1) % images.length);
    }
  }, [selectedImageIndex, images.length]);

  // 터치 스와이프 핸들러
  const minSwipeDistance = 50; // 최소 스와이프 거리 (px)

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null); // 초기화
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      showNextImage(); // 왼쪽으로 스와이프 = 다음 이미지
    } else if (isRightSwipe) {
      showPrevImage(); // 오른쪽으로 스와이프 = 이전 이미지
    }
  };

  // 키보드 이벤트 (ESC, 화살표)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (selectedImageIndex === null) return;

      if (e.key === "Escape") {
        closeLightbox();
      } else if (e.key === "ArrowLeft") {
        showPrevImage();
      } else if (e.key === "ArrowRight") {
        showNextImage();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedImageIndex, closeLightbox, showPrevImage, showNextImage]);

  return (
    <section className="bg-zinc-900">
      <div className="max-w-2xl mx-auto">
        {/* 그리드 레이아웃 - 인스타 피드 스타일 */}
        <div className="grid grid-cols-3 gap-1">
          {images.map((image, index) => (
            <div
              key={index}
              className="relative aspect-square overflow-hidden cursor-pointer group"
              onClick={() => openLightbox(index)}
            >
              <img
                src={`/images/wedding_images/${image}`}
                alt={`우리의 이야기 ${index + 1}`}
                className="w-full h-full object-cover transition-all duration-300 ease-out group-hover:scale-105 group-hover:brightness-90 group-active:scale-95 group-active:brightness-75"
                loading="lazy"
              />
              {/* 클릭 시 오버레이 효과 */}
              <div className="absolute inset-0 bg-black opacity-0 group-active:opacity-20 transition-opacity duration-150" />
            </div>
          ))}
        </div>
      </div>

      {/* 라이트박스 모달 */}
      {selectedImageIndex !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center animate-fadeIn"
          onClick={closeLightbox}
        >
          {/* 닫기 버튼 */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center text-white text-3xl rounded-full bg-white/10 hover:bg-white/20 active:bg-white/30 transition-all duration-200 z-10"
            aria-label="닫기"
          >
            ×
          </button>

          {/* 이미지 카운터 */}
          <div className="absolute top-4 left-4 text-white/80 text-sm bg-black/30 px-3 py-1 rounded-full z-10">
            {selectedImageIndex + 1} / {images.length}
          </div>

          {/* 이전 버튼 */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              showPrevImage();
            }}
            className="absolute left-2 w-12 h-12 flex items-center justify-center text-white text-4xl rounded-full bg-white/10 hover:bg-white/20 active:bg-white/30 active:scale-90 transition-all duration-200 z-10"
            aria-label="이전"
          >
            ‹
          </button>

          {/* 이미지 */}
          <img
            key={selectedImageIndex}
            src={`/images/wedding_images/${images[selectedImageIndex]}`}
            alt={`우리의 이야기 ${selectedImageIndex + 1}`}
            className="max-w-[90%] max-h-[85%] object-contain animate-fadeInSlide rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          />

          {/* 다음 버튼 */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              showNextImage();
            }}
            className="absolute right-2 w-12 h-12 flex items-center justify-center text-white text-4xl rounded-full bg-white/10 hover:bg-white/20 active:bg-white/30 active:scale-90 transition-all duration-200 z-10"
            aria-label="다음"
          >
            ›
          </button>

          {/* 하단 썸네일 인디케이터 */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
            {images.map((_, idx) => (
              <button
                key={idx}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedImageIndex(idx);
                }}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  idx === selectedImageIndex ? "bg-white w-6" : "bg-white/40 hover:bg-white/60"
                }`}
                aria-label={`이미지 ${idx + 1}`}
              />
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

// 6. 캘린더 영역
const CalendarSection = () => (
  <section style={{ backgroundColor: "#F6C4C7" }}>
    <img src="/images/calendar.jpeg" alt="Wedding Calendar" className="w-full max-w-2xl mx-auto h-auto block" />
  </section>
);

// 7. 카카오 지도 영역
const LocationInfo = () => {
  const mapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    console.log("🗺️ LocationInfo useEffect 시작");
    console.log("window.kakao 존재:", !!window.kakao);
    console.log("window.kakao.maps 존재:", !!(window.kakao && window.kakao.maps));

    if (window.kakao && window.kakao.maps) {
      const kakao = window.kakao;

      kakao.maps.load(() => {
        console.log("✅ kakao.maps.load 완료");
        if (!mapRef.current) {
          console.error("❌ mapRef.current가 없습니다");
          return;
        }

        // 카카오맵 공식 좌표 (WCONGNAMUL -> WGS84 변환)
        // urlX=464504.9999999995, urlY=1127990.0000000023
        const position = new kakao.maps.LatLng(37.5602, 126.8393);
        console.log("📍 Position:", position);

        const mapOption = {
          center: position,
          level: 4,
        };

        const map = new kakao.maps.Map(mapRef.current, mapOption);
        console.log("🗺️ Map 객체 생성 완료");

        const marker = new kakao.maps.Marker({
          position: position,
        });
        marker.setMap(map);
        console.log("📌 Marker 추가 완료");
      });
    } else {
      console.error("❌ Kakao Maps SDK가 로드되지 않았습니다");
    }
  }, []);

  // 네이버 지도 열기 (도착지: 더베뉴지서울 길찾기)
  const handleNaverMap = () => {
    const url = `https://map.naver.com/p/directions/-/14119685.8655612,4517480.1303825,%EB%8D%94%EB%B2%A0%EB%89%B4%EC%A7%80%EC%84%9C%EC%9A%B8,33499928,PLACE_POI/-/car?c=15.00,0,0,0,dh`;
    window.open(url, "_blank");
  };

  // 카카오맵 열기
  const handleKakaoMap = () => {
    // 카카오맵 공식 좌표 사용
    const url = `https://map.kakao.com/?from=roughmap&eName=%EB%8D%94%EB%B2%A0%EB%89%B4%EC%A7%80%EC%84%9C%EC%9A%B8&eX=464504.9999999995&eY=1127990.0000000023`;
    window.open(url, "_blank");
  };

  return (
    <section style={{ backgroundColor: "#ffffff" }} className="bg-white">
      <div className="max-w-md mx-auto px-4 py-8">
        <img src="/images/오시는길.png" alt="오시는길" className="w-32 mx-auto mb-4" />

        {/* 카카오맵 - 인터랙티브 지도 */}
        <div ref={mapRef} className="w-full h-64 rounded-lg mb-6 bg-white"></div>

        <div className="text-center space-y-6">
          <div>
            <p className="text-base text-gray-900 mb-1">
              <strong className="font-elegant text-lg font-bold">더 베뉴지 서울</strong>
            </p>
            <p className="text-gray-600 text-sm font-bold">서울 강서구 강서로 388</p>
            <p className="text-gray-600 text-sm">B1 마리아칼라스 홀</p>
          </div>

          {/* 네이버 지도 & 카카오맵 버튼 */}
          <div className="flex gap-8 justify-center items-start">
            {/* 카카오 버튼 */}
            <button
              onClick={handleKakaoMap}
              className="flex flex-col items-center gap-2 hover:opacity-80 transition-opacity"
            >
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-md">
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
                    fill="#2196F3"
                  />
                </svg>
              </div>
              <span className="text-gray-600 text-sm">카카오</span>
            </button>

            {/* 네이버 버튼 */}
            <button
              onClick={handleNaverMap}
              className="flex flex-col items-center gap-2 hover:opacity-80 transition-opacity"
            >
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-md">
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
                    fill="#4CAF50"
                  />
                </svg>
              </div>
              <span className="text-gray-600 text-sm">네이버</span>
            </button>
          </div>

          {/* 교통 안내 */}
          <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-6 text-center">
            <div className="space-y-6">
              {/* 지하철 */}
              <div>
                <h4 className="text-gray-900 font-medium mb-2 font-title">지하철</h4>
                <p className="text-gray-600 text-sm leading-relaxed">발산역(5호선) 3번 출구 도보 약 3분</p>
              </div>

              {/* 버스 */}
              <div>
                <h4 className="text-gray-900 font-medium mb-2 font-title">버스</h4>
                <p className="text-gray-600 text-sm leading-relaxed">발산역(중) 역 하차, 도보 약 3분</p>
              </div>

              {/* 자가용 */}
              <div>
                <h4 className="text-gray-900 font-medium mb-2 font-title">자가용</h4>
                <p className="text-gray-600 text-sm leading-relaxed">
                  건물 내 주차장 이용 가능
                  <br />
                  (3시간 무료)
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// 9. 참석 여부 영역 (RSVP)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const RSVPSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    attendance: "",
    guests: "1",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");

  // 결혼식 날짜 체크 (2026년 2월 7일)
  const weddingDate = new Date("2026-02-07T23:59:59"); // 결혼식 당일 끝까지 허용
  const today = new Date();
  const isExpired = today > weddingDate;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus("idle");

    console.log("🚀 RSVP 전송 시작");
    console.log("📝 폼 데이터:", formData);
    console.log("🔧 환경변수 확인:");
    console.log("  - Google Sheets URL:", process.env.REACT_APP_GOOGLE_SCRIPT_URL);
    console.log("  - Slack Webhook URL:", process.env.REACT_APP_SLACK_WEBHOOK_URL);

    try {
      // Google Apps Script로 전송 (Sheets 저장 + Slack 알림 모두 처리)
      if (!process.env.REACT_APP_GOOGLE_SCRIPT_URL) {
        throw new Error("Google Script URL이 설정되지 않았습니다.");
      }

      console.log("📊 RSVP 전송 중...");
      await saveToGoogleSheets(formData);
      console.log("✅ 전송 완료 (Sheets 저장 + Slack 알림)");

      // 성공 처리
      setSubmitStatus("success");
      setFormData({ name: "", attendance: "", guests: "1" });

      // 3초 후 성공 메시지 숨김
      setTimeout(() => setSubmitStatus("idle"), 3000);
    } catch (error) {
      console.error("❌ RSVP 전송 실패:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="bg-zinc-900">
      <div className="max-w-md mx-auto px-4 py-8">
        <h2 className="text-xl font-light text-white text-center mb-6">참석 여부 전달</h2>

        {/* 만료 안내 메시지 */}
        {isExpired && (
          <div className="mb-6 bg-gray-500/10 border border-gray-500 rounded-lg p-4 text-center">
            <p className="text-gray-300 text-lg mb-2">참석 여부 전달이 마감되었습니다</p>
            <p className="text-gray-400 text-sm">결혼식에 참석해주신 모든 분들께 감사드립니다.</p>
          </div>
        )}

        {/* 성공 메시지 */}
        {submitStatus === "success" && (
          <div className="mb-6 bg-primary-pink/10 border border-primary-pink rounded-lg p-4 text-center animate-fadeIn">
            <p className="text-primary-pink text-lg mb-2">✓ 참석 의사가 전달되었습니다!</p>
            <p className="text-gray-300 text-sm">소중한 마음 감사합니다.</p>
          </div>
        )}

        {/* 에러 메시지 */}
        {submitStatus === "error" && (
          <div className="mb-6 bg-red-500/10 border border-red-500 rounded-lg p-4 text-center animate-fadeIn">
            <p className="text-red-500 text-lg mb-2">✗ 전송에 실패했습니다</p>
            <p className="text-gray-300 text-sm">다시 시도해주세요.</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-gray-300 mb-2">성함</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white text-base rounded-lg focus:border-primary-pink focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed placeholder-gray-400"
              maxLength={50}
              disabled={isExpired}
              required
            />
          </div>

          <div>
            <label className="block text-sm text-gray-300 mb-2">참석 여부</label>
            <select
              value={formData.attendance}
              onChange={(e) => setFormData({ ...formData, attendance: e.target.value })}
              className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white text-base rounded-lg focus:border-primary-pink focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isExpired}
              required
            >
              <option value="">선택해주세요</option>
              <option value="yes">참석</option>
              <option value="no">불참</option>
            </select>
          </div>

          <div>
            <label className="block text-sm text-gray-300 mb-2">동반 인원</label>
            <select
              value={formData.guests}
              onChange={(e) => setFormData({ ...formData, guests: e.target.value })}
              className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white text-base rounded-lg focus:border-primary-pink focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isExpired}
            >
              <option value="1">1명 (본인)</option>
              <option value="2">2명</option>
              <option value="3">3명</option>
              <option value="4">4명</option>
            </select>
          </div>

          <button
            type="submit"
            disabled={isSubmitting || !formData.name || !formData.attendance || isExpired}
            className="w-full py-3 bg-primary-pink text-white rounded-lg hover:bg-primary-pink-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? "전송 중..." : "전송하기"}
          </button>
        </form>
      </div>
    </section>
  );
};

// 아코디언 아이템 컴포넌트 (부드러운 트랜지션)
interface AccountItemProps {
  label: string;
  isOpen: boolean;
  onToggle: () => void;
  bankName: string;
  accountNumber: string;
  accountHolder: string;
  onCopy: () => void;
}

const AccountItem = ({ label, isOpen, onToggle, bankName, accountNumber, accountHolder, onCopy }: AccountItemProps) => {
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = () => {
    onCopy();
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  return (
    <div className="mb-3 bg-gray-50 rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        aria-expanded={isOpen}
        className="w-full px-4 py-3 text-left text-gray-900 hover:bg-gray-100 transition-colors flex justify-between items-center"
      >
        <span>{label}</span>
        <span className={`text-primary-pink transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}>▼</span>
      </button>
      <div
        className={`grid transition-all duration-300 ease-in-out ${
          isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden">
          <div className="px-4 pb-4 space-y-2 border-t border-gray-200">
            <p className="text-gray-600 text-sm mt-3">은행명: {bankName}</p>
            <p className="text-gray-600 text-sm">계좌번호: <span className="font-mono tracking-wide">{accountNumber}</span></p>
            <p className="text-gray-600 text-sm">예금주: {accountHolder}</p>
            <button
              onClick={handleCopy}
              className={`w-full mt-2 px-4 py-2 text-white text-sm rounded transition-all duration-300 flex items-center justify-center gap-2 ${
                isCopied ? "bg-green-500" : "bg-primary-pink hover:bg-primary-pink-dark"
              }`}
            >
              {isCopied ? (
                <>
                  <Check size={16} />
                  복사 완료
                </>
              ) : (
                <>
                  <Copy size={16} />
                  계좌번호 복사
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// 8. 계좌번호 영역
const AccountSection = () => {
  // 각 계좌 항목의 펼침/접힘 상태
  const [openAccounts, setOpenAccounts] = useState<{ [key: string]: boolean }>({
    groom: false,
    groomFather: false,
    groomMother: false,
    bride: false,
    brideFather: false,
    brideMother: false,
  });

  // 아코디언 토글
  const toggleAccount = (key: string) => {
    setOpenAccounts((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // 계좌번호 복사
  const copyToClipboard = (accountNumber: string) => {
    navigator.clipboard.writeText(accountNumber);
  };

  return (
    <section style={{ backgroundColor: "#ffe4e6" }} className="bg-pink-custom">
      <div className="max-w-md mx-auto px-4 pt-8 pb-24">
        <h2 className="text-xl font-bold text-gray-900 text-center mb-6 font-classic">마음 전하실 곳</h2>

        {/* 신랑측 */}
        <div className="mb-6">
          <h3 className="text-primary-pink text-center mb-4 text-lg font-bold font-title">신랑측</h3>

          <AccountItem
            label="신랑"
            isOpen={openAccounts.groom}
            onToggle={() => toggleAccount("groom")}
            bankName="신한은행"
            accountNumber="110-389-215693"
            accountHolder="김승태"
            onCopy={() => copyToClipboard("110-389-215693")}
          />

          <AccountItem
            label="신랑 아버지"
            isOpen={openAccounts.groomFather}
            onToggle={() => toggleAccount("groomFather")}
            bankName="국민은행"
            accountNumber="520702-01-221544"
            accountHolder="김종일"
            onCopy={() => copyToClipboard("520702-01-221544")}
          />

          <AccountItem
            label="신랑 어머니"
            isOpen={openAccounts.groomMother}
            onToggle={() => toggleAccount("groomMother")}
            bankName="국민은행"
            accountNumber="520725-93-107246"
            accountHolder="정미진"
            onCopy={() => copyToClipboard("520725-93-107246")}
          />
        </div>

        {/* 신부측 */}
        <div>
          <h3 className="text-primary-pink text-center mb-4 text-lg font-bold font-title">신부측</h3>

          <AccountItem
            label="신부"
            isOpen={openAccounts.bride}
            onToggle={() => toggleAccount("bride")}
            bankName="신한은행"
            accountNumber="110-393-865279"
            accountHolder="오수민"
            onCopy={() => copyToClipboard("110-393-865279")}
          />

          <AccountItem
            label="신부 아버지"
            isOpen={openAccounts.brideFather}
            onToggle={() => toggleAccount("brideFather")}
            bankName="농협은행"
            accountNumber="601105-52-056638"
            accountHolder="조형근"
            onCopy={() => copyToClipboard("601105-52-056638")}
          />

          <AccountItem
            label="신부 어머니"
            isOpen={openAccounts.brideMother}
            onToggle={() => toggleAccount("brideMother")}
            bankName="국민은행"
            accountNumber="671401-01-256700"
            accountHolder="이순희"
            onCopy={() => copyToClipboard("671401-01-256700")}
          />
        </div>
      </div>
    </section>
  );
};

// RSVP Floating Button Component
const RSVPFloatingButton = ({ onClick }: { onClick: () => void }) => {
  return (
    <div className="fixed bottom-6 left-6 z-50 hidden">
      <button
        onClick={onClick}
        className="px-4 py-3 bg-primary-pink hover:bg-primary-pink-dark rounded-full shadow-lg flex items-center gap-2 transition-colors text-white font-medium"
      >
        <span className="text-lg">✉️</span>
        <span className="text-sm">참석 여부</span>
      </button>
    </div>
  );
};

// RSVP Modal Component
const RSVPModal = ({ onClose }: { onClose: () => void }) => {
  const [formData, setFormData] = useState({
    name: "",
    attendance: "",
    guests: "1",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");

  // 결혼식 날짜 체크 (2026년 2월 7일)
  const weddingDate = new Date("2026-02-07T23:59:59");
  const today = new Date();
  const isExpired = today > weddingDate;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus("idle");

    try {
      if (!process.env.REACT_APP_GOOGLE_SCRIPT_URL) {
        throw new Error("Google Script URL이 설정되지 않았습니다.");
      }

      // Google Sheets에 저장 (Slack 알림은 Apps Script에서 자동 전송)
      await saveToGoogleSheets(formData);

      setSubmitStatus("success");
      setFormData({ name: "", attendance: "", guests: "1" });

      // 2초 후 모달 자동 닫기
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error("❌ RSVP 전송 실패:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-75 flex items-center justify-center p-4 animate-backdropFadeIn" onClick={onClose}>
      <div
        className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto font-pretendard animate-modalSlideDown"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-medium text-gray-900">참석 여부 전달</h2>
          <button
            onClick={onClose}
            className="text-gray-900 text-3xl hover:text-gray-600 leading-none"
            aria-label="닫기"
          >
            ×
          </button>
        </div>

        {/* 내용 */}
        <div className="px-6 py-6">
          {/* 만료 안내 메시지 */}
          {isExpired && (
            <div className="mb-6 bg-gray-100 border border-gray-300 rounded-lg p-4 text-center">
              <p className="text-gray-700 text-lg mb-2">참석 여부 전달이 마감되었습니다</p>
              <p className="text-gray-600 text-sm">결혼식에 참석해주신 모든 분들께 감사드립니다.</p>
            </div>
          )}

          {/* 성공 메시지 */}
          {submitStatus === "success" && (
            <div className="mb-6 bg-primary-pink/10 border border-primary-pink rounded-lg p-4 text-center animate-fadeIn">
              <p className="text-primary-pink text-lg mb-2">✓ 참석 의사가 전달되었습니다!</p>
              <p className="text-gray-700 text-sm">소중한 마음 감사합니다.</p>
            </div>
          )}

          {/* 에러 메시지 */}
          {submitStatus === "error" && (
            <div className="mb-6 bg-red-500/10 border border-red-500 rounded-lg p-4 text-center animate-fadeIn">
              <p className="text-red-500 text-lg mb-2">✗ 전송에 실패했습니다</p>
              <p className="text-gray-700 text-sm">다시 시도해주세요.</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-gray-700 mb-2">성함</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-3 bg-white border border-gray-300 text-gray-900 text-base rounded-lg focus:border-primary-pink focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed placeholder-gray-500"
                maxLength={50}
                disabled={isExpired}
                required
              />
            </div>

            <div>
              <label className="block text-sm text-gray-700 mb-2">참석 여부</label>
              <select
                value={formData.attendance}
                onChange={(e) => setFormData({ ...formData, attendance: e.target.value })}
                className="w-full px-4 py-3 bg-white border border-gray-300 text-gray-900 text-base rounded-lg focus:border-primary-pink focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isExpired}
                required
              >
                <option value="">선택해주세요</option>
                <option value="yes">참석</option>
                <option value="no">불참</option>
              </select>
            </div>

            <div>
              <label className="block text-sm text-gray-700 mb-2">동반 인원</label>
              <select
                value={formData.guests}
                onChange={(e) => setFormData({ ...formData, guests: e.target.value })}
                className="w-full px-4 py-3 bg-white border border-gray-300 text-gray-900 text-base rounded-lg focus:border-primary-pink focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isExpired}
              >
                <option value="1">1명 (본인)</option>
                <option value="2">2명</option>
                <option value="3">3명</option>
                <option value="4">4명</option>
              </select>
            </div>

            <button
              type="submit"
              disabled={isSubmitting || !formData.name || !formData.attendance || isExpired}
              className="w-full py-3 bg-primary-pink text-white rounded-lg hover:bg-primary-pink-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "전송 중..." : "전송하기"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// Music Control Component
const MusicControl = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const hasPlayedRef = useRef(false);
  const listenersAddedRef = useRef(false);

  useEffect(() => {
    const audio = new Audio("/music/나랑아니면.mp3");
    audio.preload = "auto";
    audio.loop = false;
    audio.volume = 0.3;
    audioRef.current = audio;

    const handleEnded = () => setIsPlaying(false);
    audio.addEventListener("ended", handleEnded);

    const removeAllListeners = () => {
      document.removeEventListener("click", handleInteraction);
      document.removeEventListener("touchstart", handleInteraction);
      document.removeEventListener("scroll", handleInteraction);
      document.removeEventListener("touchmove", handleInteraction);
      document.removeEventListener("keydown", handleInteraction);
      listenersAddedRef.current = false;
    };

    const handleInteraction = () => {
      if (hasPlayedRef.current || !audioRef.current) return;
      hasPlayedRef.current = true;

      audioRef.current
        .play()
        .then(() => {
          setIsPlaying(true);
          removeAllListeners();
        })
        .catch(() => {
          hasPlayedRef.current = false;
        });
    };

    const setupInteractionListeners = () => {
      if (listenersAddedRef.current) return;
      listenersAddedRef.current = true;

      document.addEventListener("click", handleInteraction);
      document.addEventListener("touchstart", handleInteraction, { passive: true });
      document.addEventListener("scroll", handleInteraction, { passive: true });
      document.addEventListener("touchmove", handleInteraction, { passive: true });
      document.addEventListener("keydown", handleInteraction);
    };

    // 카카오톡 인앱 브라우저: 링크 클릭 직후 짧은 시간 동안 자동재생 허용됨
    // 최대한 빨리 play() 시도해야 함
    const tryAutoPlay = () => {
      if (hasPlayedRef.current) return;

      audio
        .play()
        .then(() => {
          hasPlayedRef.current = true;
          setIsPlaying(true);
        })
        .catch(() => {
          setupInteractionListeners();
        });
    };

    // 1. 즉시 재생 시도 (카카오톡 인앱 브라우저 대응)
    tryAutoPlay();

    // 2. 오디오 로드 후 다시 시도 (로드가 늦은 경우 대비)
    audio.addEventListener("canplay", tryAutoPlay, { once: true });

    // 3. 페이지 완전 로드 후 다시 시도
    if (document.readyState === "complete") {
      tryAutoPlay();
    } else {
      window.addEventListener("load", tryAutoPlay, { once: true });
    }

    return () => {
      audio.removeEventListener("ended", handleEnded);
      audio.pause();
      audioRef.current = null;
      removeAllListeners();
    };
  }, []);

  const togglePlay = () => {
    if (isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
    } else {
      if (audioRef.current) {
        // 음악이 끝났다면 처음부터 다시 재생
        if (audioRef.current.ended) {
          audioRef.current.currentTime = 0;
        }
        audioRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  return (
    <div className="fixed top-6 right-6 z-50">
      <button
        onClick={togglePlay}
        className="w-8 h-8 bg-primary-pink hover:bg-primary-pink-dark rounded-full shadow-lg flex items-center justify-center transition-colors p-1.5"
      >
        <img
          src={isPlaying ? "/images/sound_on.png" : "/images/sound_off.png"}
          alt={isPlaying ? "Pause Music" : "Play Music"}
          className="w-7 h-7 object-contain"
        />
      </button>
    </div>
  );
};

export default App;
